import React, { useEffect, useState, useRef, useMemo } from 'react';
import { FaInstagram, FaTwitch, FaSoundcloud, FaYoutube, FaSpotify, FaBandcamp, FaMixcloud, FaFacebookF, FaTwitter, FaGlobe } from 'react-icons/fa';
import { getRandomArtistImage, preloadImage } from '../utils/imageUtils';
import '../styles/ArtistCard.css';

const ArtistCard = ({ artist }) => {
  const { name, bio, social_links, talents } = artist;
  const [artistImage, setArtistImage] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const audioContextRef = useRef(null);

  const socialIcons = {
    instagram: FaInstagram,
    twitch: FaTwitch,
    soundcloud: FaSoundcloud,
    youtube: FaYoutube,
    spotify: FaSpotify,
    bandcamp: FaBandcamp,
    mixcloud: FaMixcloud,
    facebook: FaFacebookF,
    twitter: FaTwitter,
    website: FaGlobe
  };

  const handleUserInteraction = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      console.log('AudioContext created');
    } else {
      audioContextRef.current.resume().then(() => {
        console.log('AudioContext resumed');
      });
    }
  };

  useEffect(() => {
    const loadRandomImage = async () => {
      try {
        const imageUrl = await getRandomArtistImage(name.toLowerCase());
        if (imageUrl) {
          await preloadImage(imageUrl);
          setArtistImage(imageUrl);
          setImageLoaded(true);
        }
      } catch (error) {
        console.error('Failed to load artist image:', error);
      }
    };
    
    loadRandomImage();
  }, [name]);

  const plainBio = useMemo(() => {
    if (!bio) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = bio;
    return tmp.textContent || tmp.innerText || '';
  }, [bio]);

  const renderSocialLinks = () => {
    return Object.entries(social_links || {}).map(([platform, url]) => {
      if (!url) return null;
      const Icon = socialIcons[platform.toLowerCase()];
      return Icon ? (
        <a
          key={platform}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="social-link"
          title={platform.charAt(0).toUpperCase() + platform.slice(1)}
        >
          <Icon />
        </a>
      ) : null;
    });
  };

  return (
    <div className="artist-card" onClick={handleUserInteraction}>
      <div className="artist-image">
        {imageLoaded ? (
          <img src={artistImage} alt={name} loading="lazy" />
        ) : (
          <div className="artist-image-placeholder" />
        )}
      </div>
      <div className="artist-content">
        <div className="artist-info">
          <h1>{name}</h1>
          <p className="artist-talents">{Array.isArray(talents) ? talents.join(', ') : talents}</p>
          <div className="artist-social-links">
            {renderSocialLinks()}
          </div>
          <p className="artist-bio">{plainBio}</p>
        </div>
      </div>
    </div>
  );
};

export default ArtistCard;
